// imports
import { Component } from 'react'
import { Autocomplete, Box, styled, TextField } from '@mui/material'
import { IFormInputBase } from './types'
import { getErrorMessage } from './helpers'
import { ApiAlertContext } from '../api-context'
import { RichTooltip, HelpOutlinePulseIcon } from '../components/rich-tooltip'
// types
import type { ComponentClass } from 'react'
import type { FilterOptionsState, TextFieldProps, AutocompleteRenderOptionState } from '@mui/material'
import type { IRichTooltipPanelConfig } from '../components/rich-tooltip'

export interface ISelectWithApiOption<TOther = any> {
  Id: string
  Name?: string
  Other?: TOther
}

export interface ISelectWithApiPropsSlots<O extends ISelectWithApiOption = ISelectWithApiOption> {
  textFieldProps: TextFieldProps
  renderOption?: (props: React.HTMLAttributes<HTMLElement>, option: O, state: AutocompleteRenderOptionState) => React.ReactNode
}

export type ISelectWithApiFetchData<O extends ISelectWithApiOption> = (value?: string, signal?: AbortSignal) => Promise<O[]>

export interface ISelectWithApiProps<T, O extends ISelectWithApiOption> extends IFormInputBase<T>, ISelectWithApiParams<O> {
  tooltip?: IRichTooltipPanelConfig
  onChange?: (value: O | null) => void
  existedIds?: string[]
  slots?: ISelectWithApiPropsSlots<O>
}

export interface ISelectWithApiState<O extends ISelectWithApiOption> {
  options: O[]
  statusText?: string
  optionSelected: O | null
  inputValue: string
  loading?: boolean
}

export interface ISelectWithApiParams<O extends ISelectWithApiOption> {
  fetchData?: ISelectWithApiFetchData<O>
  tooltip?: IRichTooltipPanelConfig
  renderOption?: (props: React.HTMLAttributes<HTMLElement>, option: O, state: AutocompleteRenderOptionState) => React.ReactNode
}

export interface ISelectWithApiHandle {
  clear: () => void
}

function createSelectWithApi<T, O extends ISelectWithApiOption = ISelectWithApiOption>(params?: ISelectWithApiParams<O>) {
  class SelectWithApi extends Component<ISelectWithApiProps<T, O>, ISelectWithApiState<O>> implements ISelectWithApiHandle {
    abortController = { signalController: new AbortController() }
    refInput: HTMLInputElement | null = null
    existedIds: string[] = []
    constructor(props: ISelectWithApiProps<T, O>) {
      super(props)
      this.state = {
        options: [],
        statusText: 'no items',
        optionSelected: null,
        inputValue: '',
        loading: true
      }
      this.existedIds = props.existedIds ?? []
    }

    componentDidMount() {
      this.fetchData()
    }

    componentWillUnmount(): void {
      this.timer.clear()
    }

    componentDidUpdate(prevProps: Readonly<ISelectWithApiProps<T, O>>): void {
      if (JSON.stringify(prevProps.existedIds) !== JSON.stringify(this.props.existedIds)) {
        this.existedIds = this.props.existedIds ?? []
      }
    }

    render() {
      const defaultValue = this.getDefaultValue()
      const eMessage = getErrorMessage(this.props.messageErrors, this.props.name)
      const renderOption = this.getWrappedRenderOption()
      return (
        <SelectWithApiStyled className={this.getRootClasses()}>
          <Autocomplete
            disabled={this.state.loading || this.props.disabled}
            fullWidth
            noOptionsText={this.state.statusText}
            options={this.state.options}
            getOptionLabel={(x) => x.Name ?? x.Id}
            getOptionKey={(x) => JSON.stringify(x)}
            isOptionEqualToValue={(o, v) => o.Id.toString() === v.Id.toString() && o.Name === v.Name}
            filterOptions={this.fillterOptions}
            renderOption={renderOption}
            // select
            value={this.state.optionSelected}
            onChange={this.handleChange}
            // input
            inputValue={this.state.inputValue}
            onInputChange={this.handleInputChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label={this.getLabel()}
                error={eMessage.error}
                helperText={eMessage.message}
                onBlur={() => {
                  if (!this.props.name) return
                  this.props.onBlur && this.props.onBlur(this.props.name)
                }}
                {...this.props.slots?.textFieldProps}
              />
            )}
          />
          <input ref={(ref) => (this.refInput = ref)} hidden name={this.props.name?.toString()} defaultValue={defaultValue} />
          {this.state.optionSelected?.Other && (
            <input
              hidden
              name={`${this.props.name?.toString()}Other`}
              key={this.state.optionSelected.Id ?? 'key'}
              defaultValue={JSON.stringify(this.state.optionSelected.Other)}
            />
          )}
          {this.renderTooltip()}
        </SelectWithApiStyled>
      )
    }

    renderTooltip = () => {
      const tooltip = this.props.tooltip || params?.tooltip
      if (!tooltip) return null
      return (
        <div className={selectWithApiClasses.tooltipWrap}>
          <RichTooltip panel={tooltip}>
            <HelpOutlinePulseIcon fontSize='small' />
          </RichTooltip>
        </div>
      )
    }

    getRootClasses = () => {
      const classes = [selectWithApiClasses.root]
      const tooltip = this.props.tooltip || params?.tooltip
      if (tooltip) classes.push(selectWithApiClasses.tooltip)
      return classes.join(' ')
    }

    getFetchDataFunc = (): ISelectWithApiFetchData<O> => {
      return this.props.fetchData ?? params?.fetchData ?? (() => Promise.resolve([]))
    }

    getWrappedRenderOption = () => {
      const userRenderOption = this.props.slots?.renderOption ?? params?.renderOption
      if (!userRenderOption) return undefined

      return (props: any, option: O, state: AutocompleteRenderOptionState) => {
        const { key, ...propsWithoutKey } = props
        return (
          <li key={key} {...(propsWithoutKey as any)} style={{ width: '100%', boxSizing: 'border-box', ...propsWithoutKey.style }}>
            {userRenderOption(propsWithoutKey as React.HTMLAttributes<HTMLElement>, option, state)}
          </li>
        )
      }
    }

    timer = {
      _timer: 0,
      _second: 500,
      callback: async (value: string) => {
        try {
          this.abortController.signalController = new AbortController()
          const res = await this.getFetchDataFunc()(value, this.abortController.signalController.signal)
          const options = this.getOptionsFilter(res, this.existedIds)
          this.setState({ options })
        } catch (error) {
          // console.log(error)
          ApiAlertContext.ApiAlert?.PushError('Error from server!')
        } finally {
          this.setState({ statusText: 'no items' })
        }
      },
      start: (text: string) => {
        this.timer.clear()
        this.timer._timer = window.setTimeout(() => this.timer.callback(text), this.timer._second)
      },
      clear: () => {
        this.abortController.signalController.abort()
        clearTimeout(this.timer._timer)
      }
    }

    fetchData = async () => {
      try {
        const defaultValue = this.getDefaultValue()
        const res = await this.getFetchDataFunc()(defaultValue, this.abortController.signalController.signal)
        if (!Array.isArray(res)) return
        const options = this.getOptionsFilter(res, this.existedIds)
        const optionSelected = options.find((x) => x.Id === defaultValue) ?? null
        this.setState({ options, optionSelected, loading: false })
        return
      } catch (error) {
        // console.log(error)
        ApiAlertContext.ApiAlert?.PushError('Error from server!')
      } finally {
        this.setState({ statusText: 'no items', loading: false })
      }
    }

    handleChange = (_: React.SyntheticEvent, value: O | null) => {
      this.setState({ optionSelected: value })
      if (this.refInput) this.refInput.value = value?.Id ?? ''
      this.props.onChange && this.props.onChange(value)
    }

    handleInputChange = (_: React.SyntheticEvent, value: string) => {
      const state: Pick<ISelectWithApiState<O>, 'inputValue' | 'statusText' | 'loading'> = { inputValue: value }
      if (value === this.state.optionSelected?.Name) {
        this.setState(state)
        return
      }
      const valueFormated = value.trim().toLowerCase()
      const selectedIndex = this.state.options.findIndex((x) => {
        return x.Name?.trim().toLowerCase().includes(valueFormated)
      })
      if (selectedIndex < 0 || valueFormated === '') state.statusText = 'loading...'
      this.setState(state, () => {
        if (selectedIndex < 0 || valueFormated === '') this.timer.start(valueFormated)
      })
    }

    fillterOptions = (options: O[], state: FilterOptionsState<O>) => {
      return options.filter((x) => {
        const value = state.inputValue.toLowerCase()
        return x.Id.toLowerCase().includes(value) || x.Name?.toLowerCase().includes(value)
      })
    }

    getLabel = () => {
      return this.props.label ?? this.props.name?.toString()
    }

    getDefaultValue = () => {
      if (!this.props.name) return
      return (this.props.defaultValue ?? this.props.data?.[this.props.name])?.toString()
    }

    getOptionsFilter<O extends ISelectWithApiOption = ISelectWithApiOption>(options: O[], existedId: string[] = []): O[] {
      const ids = new Set<string | number>(existedId)
      return options.reduce<O[]>((a, b) => {
        if (!ids.has(b.Id)) {
          a.push(b)
          ids.add(b.Id)
        }
        return a
      }, [])
    }

    clear = () => {
      this.setState({ optionSelected: null, inputValue: '' })
      if (this.refInput) this.refInput.value = ''
    }
  }
  return SelectWithApi
}
export default createSelectWithApi

const selectWithApiClasses = {
  root: 'SelectWithApi-root',
  tooltip: 'SelectWithApi-tooltip',
  tooltipWrap: 'SelectWithApi-tooltipWrap'
}

const getSelectWithApiClass = (key: keyof typeof selectWithApiClasses, options?: { prefix?: string; suffix?: string }) => {
  return `${options?.prefix || ''}.${selectWithApiClasses[key]}${options?.suffix || ''}`
}

const SelectWithApiStyled = styled(Box)(({ theme }) => ({
  [getSelectWithApiClass('root', { suffix: '&' })]: {
    width: '100%',
    position: 'relative'
  },
  [getSelectWithApiClass('tooltipWrap')]: {
    position: 'absolute',
    top: theme.spacing(2.25),
    right: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  [getSelectWithApiClass('tooltip', { suffix: '&' })]: {
    '.MuiInputBase-root .MuiAutocomplete-endAdornment': {
      right: theme.spacing(4.5)
    }
  }
}))
